// Lightweight error boundary — prevents render crashes from blanking the page.
// Brand principle: errors stay neutral. The brand color (coral) is associated
// with action and good outcomes, never with error states. Fallback uses only
// muted-foreground / outline buttons — no coral, no red, no destructive tints.

import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  retry = () => this.setState({ hasError: false });
  refresh = () => window.location.reload();

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="flex flex-1 items-center justify-center p-8">
            <div className="max-w-md text-center space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Couldn't load this page. Try again, or refresh if it persists.
              </p>
              <div className="flex items-center justify-center gap-2">
                <Button variant="outline" size="sm" onClick={this.retry}>
                  Try again
                </Button>
                <Button variant="outline" size="sm" onClick={this.refresh}>
                  Refresh page
                </Button>
              </div>
            </div>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
